<script setup lang="ts">
// Report a file in a library to this server's admins (HRTH-94). For an encrypted library,
// the reporter can include the file itself: only that one file's key is shared, never the
// library's, and the server checks it opens the stored file.
import { ref } from 'vue'
import type { ReportReason, ReportedFile } from '../api/types'
import { describeError } from '../api/http'
import { REASONS } from '../lib/reports'
import { useSession } from '../stores/session'
import Modal from './Modal.vue'

const props = defineProps<{
  library: string
  entry: string
  path: string
  title?: string
  artist?: string
  /** The file and its key, if this device has them (encrypted libraries). */
  file?: ReportedFile
}>()
const emit = defineEmits<{ close: [] }>()
const session = useSession()
const reason = ref<ReportReason | null>(null)
const note = ref('')
const include = ref(true)
const busy = ref(false)

async function send(): Promise<void> {
  if (!reason.value) return
  busy.value = true
  try {
    await session.api.reportLibrary({
      library: props.library,
      entry: props.entry,
      path: props.path,
      ...(props.title ? { title: props.title } : {}),
      ...(props.artist ? { artist: props.artist } : {}),
      ...(props.file && include.value ? { file: props.file } : {}),
      reason: reason.value,
      ...(note.value.trim() ? { note: note.value.trim() } : {}),
    })
    session.toast('Reported to the server admins. Thank you')
    emit('close')
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <Modal title="Report file" @close="emit('close')">
    <p class="mb-3 text-sm text-text-secondary">
      Reporting <span class="mono">{{ path }}</span> to this server's admins. The person who uploaded it isn't told who
      reported it.
    </p>
    <form class="space-y-3" data-testid="library-report-form" @submit.prevent="send">
      <fieldset class="space-y-1">
        <legend class="label">Reason</legend>
        <label v-for="r in REASONS" :key="r.reason" class="flex cursor-pointer items-start gap-2 py-0.5 text-sm">
          <input v-model="reason" type="radio" name="reason" :value="r.reason" class="mt-1" :data-testid="`report-reason-${r.reason}`" />
          <span>
            {{ r.label }}
            <span class="mono block text-text-muted">{{ r.hint }}</span>
          </span>
        </label>
      </fieldset>
      <label v-if="file" class="flex cursor-pointer items-start gap-2 text-sm">
        <input v-model="include" type="checkbox" class="mt-1" data-testid="report-include-file" />
        <span>
          Include the file so admins can review it
          <span class="mono block text-text-muted">Only this file's key is shared, never the library's.</span>
        </span>
      </label>
      <label class="block">
        <span class="label">Anything to add (optional)</span>
        <textarea v-model="note" class="input min-h-16" maxlength="2000" data-testid="report-note" />
      </label>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-ghost" @click="emit('close')">Cancel</button>
        <button class="btn-primary" :disabled="busy || !reason" data-testid="report-send">{{ busy ? '…' : 'Report' }}</button>
      </div>
    </form>
  </Modal>
</template>
